import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, Save, X, Trash2, RefreshCw, Wrench, Search, RotateCcw,
} from "lucide-react";
import { toast } from "sonner";

const rp = (n) => "Rp " + (Number(n) || 0).toLocaleString("id-ID");

export default function Commissions() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState({}); // { item_id: {amount, note} }
  const [newItem, setNewItem] = useState({ item_id: "", amount: "", note: "" });
  const [adding, setAdding] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.listCommissions();
      setRows(r.rows || []);
    } catch (e) {
      toast.error("Gagal muat: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const kw = q.trim().toUpperCase();
    const base = kw
      ? rows.filter(r =>
          r.item_id.includes(kw) ||
          (r.note || "").toUpperCase().includes(kw)
        )
      : rows;
    // v84.3 — Sort by numeric suffix of item_id DESC (LC162 di atas, LC101 di bawah).
    // Extract angka dari item_id (mis. "LC101" → 101, fallback 0 kalau tak ada angka).
    const numOf = (id) => parseInt((id || "").replace(/\D+/g, ""), 10) || 0;
    return [...base].sort((a, b) => numOf(b.item_id) - numOf(a.item_id));
  }, [rows, q]);

  const totalSum = useMemo(
    () => filtered.reduce((a, b) => a + Number(b.amount || 0), 0),
    [filtered]
  );

  function startEdit(row) {
    setEditing((e) => ({ ...e, [row.item_id]: { amount: row.amount, note: row.note || "" } }));
  }
  function cancelEdit(item_id) {
    setEditing((e) => { const c = { ...e }; delete c[item_id]; return c; });
  }
  async function saveEdit(item_id) {
    const payload = editing[item_id];
    if (!payload) return;
    const amt = Number(payload.amount);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error("Nominal tidak valid");
      return;
    }
    try {
      const updated = await api.updateCommission(item_id, { amount: amt, note: payload.note || null });
      setRows((rs) => rs.map((r) => (r.item_id === item_id ? updated : r)));
      cancelEdit(item_id);
      toast.success(`${item_id} diperbarui`);
    } catch (e) {
      toast.error("Gagal simpan: " + (e?.response?.data?.detail || e.message));
    }
  }
  async function removeRow(item_id) {
    if (!window.confirm(`Hapus ${item_id}?\n\nItem lama yang refer ke ID ini akan return Rp 0 (dilewati saat kalkulasi).`)) return;
    try {
      await api.deleteCommission(item_id);
      setRows((rs) => rs.filter((r) => r.item_id !== item_id));
      toast.success(`${item_id} dihapus`);
    } catch (e) {
      toast.error("Gagal hapus: " + (e?.response?.data?.detail || e.message));
    }
  }
  async function addNew() {
    const idRaw = (newItem.item_id || "").trim().toUpperCase();
    if (!/^[A-Z]{2}\d+$/.test(idRaw)) {
      toast.error("Item ID harus format 2 huruf + angka (mis. LC181)");
      return;
    }
    const amt = Number(newItem.amount);
    if (!Number.isFinite(amt) || amt < 0) {
      toast.error("Nominal tidak valid");
      return;
    }
    try {
      const created = await api.createCommission({
        item_id: idRaw, amount: amt, note: newItem.note || null,
      });
      setRows((rs) => [...rs, created].sort((a, b) => a.item_id.localeCompare(b.item_id)));
      setNewItem({ item_id: "", amount: "", note: "" });
      setAdding(false);
      toast.success(`${idRaw} ditambahkan`);
    } catch (e) {
      toast.error("Gagal tambah: " + (e?.response?.data?.detail || e.message));
    }
  }
  async function reseed() {
    if (!window.confirm("Reset ke default 78 entri (v37)?\n\n⚠️ Semua custom edits akan HILANG.")) return;
    try {
      const r = await api.reseedCommissions();
      toast.success(`Reset selesai: ${r.count || 0} entri`);
      await load();
    } catch (e) {
      toast.error("Gagal reset: " + (e?.response?.data?.detail || e.message));
    }
  }

  return (
    <div className="space-y-4" data-testid="commissions-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
            <Wrench className="w-5 h-5 text-blue-700" />
            Tarif Jasa Teknisi
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Nominal komisi per <b>Item ID</b> di ujung nama item Tarif (mis. LC162). Perubahan langsung berlaku ke Rekap Penagihan tanpa perlu redeploy.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline" size="sm" onClick={reseed}
            className="h-9 rounded-sm gap-1.5 text-orange-700 hover:text-orange-800"
            data-testid="reseed-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" /> Reset ke Default
          </Button>
          <Button
            size="sm" onClick={() => setAdding(true)}
            className="h-9 rounded-sm gap-1.5 bg-blue-700 hover:bg-blue-800"
            data-testid="add-commission-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Tambah Item
          </Button>
        </div>
      </div>

      {/* KPI + search */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="border-2 border-blue-700 bg-blue-50 rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold">Total Entri</div>
          <div className="text-2xl font-bold text-blue-700 mt-1 font-mono" data-testid="total-entries">
            {rows.length}
          </div>
        </div>
        <div className="border border-zinc-200 bg-white rounded-sm p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Sum Filter Aktif</div>
          <div className="text-2xl font-bold text-zinc-800 mt-1 font-mono">{rp(totalSum)}</div>
          <div className="text-[10px] text-zinc-400 mt-0.5">{filtered.length} entri terlihat</div>
        </div>
        <div className="border border-zinc-200 bg-white rounded-sm p-4 flex flex-col justify-center">
          <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Cari</label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
            <Input
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="LC162 atau catatan…"
              className="pl-7 h-9 rounded-sm text-[12px]"
              data-testid="search-input"
            />
          </div>
        </div>
      </div>

      {/* Add row form */}
      {adding && (
        <div className="border-2 border-blue-400 bg-blue-50/50 rounded-sm p-4" data-testid="add-form">
          <div className="text-[10px] uppercase tracking-widest text-blue-700 font-semibold mb-2">
            Tambah Item ID Baru
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
            <Input
              value={newItem.item_id}
              onChange={(e) => setNewItem({ ...newItem, item_id: e.target.value.toUpperCase() })}
              placeholder="Item ID (mis. LC181)"
              className="h-9 rounded-sm font-mono text-[13px]"
              autoFocus
              data-testid="new-item-id-input"
            />
            <Input
              type="number" min="0"
              value={newItem.amount}
              onChange={(e) => setNewItem({ ...newItem, amount: e.target.value })}
              placeholder="Nominal (mis. 300000)"
              className="h-9 rounded-sm font-mono text-[13px]"
              data-testid="new-amount-input"
            />
            <Input
              value={newItem.note}
              onChange={(e) => setNewItem({ ...newItem, note: e.target.value })}
              placeholder="Catatan (opsional)"
              className="h-9 rounded-sm text-[12px]"
              data-testid="new-note-input"
            />
            <div className="flex gap-1">
              <Button size="sm" onClick={addNew} className="h-9 rounded-sm gap-1 bg-blue-700 hover:bg-blue-800 flex-1" data-testid="save-new-btn">
                <Save className="w-3.5 h-3.5" /> Simpan
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setAdding(false); setNewItem({ item_id: "", amount: "", note: "" }); }}
                className="h-9 rounded-sm">
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-zinc-200 rounded-sm bg-white overflow-hidden">
        <Table>
          <TableHeader className="bg-zinc-50">
            <TableRow>
              <TableHead className="w-32 font-mono">Item ID</TableHead>
              <TableHead className="text-right w-40">Nominal Jasa</TableHead>
              <TableHead>Catatan</TableHead>
              <TableHead className="w-40">Diperbarui</TableHead>
              <TableHead className="w-32 text-center">Aksi</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-center text-zinc-400 py-6">
                <RefreshCw className="w-4 h-4 animate-spin inline mr-2" /> Memuat…
              </TableCell></TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-zinc-400 py-6">
                {q ? "Tidak ada entri yang cocok" : "Belum ada entri"}
              </TableCell></TableRow>
            )}
            {!loading && filtered.map((r) => {
              const isEditing = !!editing[r.item_id];
              return (
                <TableRow key={r.item_id} data-testid={`row-${r.item_id}`}>
                  <TableCell className="font-mono font-semibold text-blue-700">{r.item_id}</TableCell>
                  <TableCell className="text-right font-mono">
                    {isEditing ? (
                      <Input
                        type="number" min="0"
                        value={editing[r.item_id].amount}
                        onChange={(e) => setEditing((s) => ({ ...s, [r.item_id]: { ...s[r.item_id], amount: e.target.value } }))}
                        className="h-8 rounded-sm text-right font-mono text-[12px]"
                        autoFocus
                        data-testid={`edit-amount-${r.item_id}`}
                      />
                    ) : (
                      <span className="font-semibold text-[12px]">{rp(r.amount)}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Input
                        value={editing[r.item_id].note}
                        onChange={(e) => setEditing((s) => ({ ...s, [r.item_id]: { ...s[r.item_id], note: e.target.value } }))}
                        className="h-8 rounded-sm text-[12px]"
                        placeholder="Catatan…"
                      />
                    ) : (
                      <span className="text-[12px] text-zinc-600">{r.note || <span className="text-zinc-400">—</span>}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-[10px] font-mono text-zinc-500">
                    {r.updated_at ? r.updated_at.slice(0, 16).replace("T", " ") : "—"}
                  </TableCell>
                  <TableCell className="text-center">
                    {isEditing ? (
                      <div className="flex justify-center gap-1">
                        <Button size="icon" onClick={() => saveEdit(r.item_id)} className="h-7 w-7 rounded-sm bg-blue-700 hover:bg-blue-800" data-testid={`save-${r.item_id}`}>
                          <Save className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => cancelEdit(r.item_id)} className="h-7 w-7 rounded-sm">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex justify-center gap-1">
                        <Button size="icon" variant="outline" onClick={() => startEdit(r)}
                          className="h-7 w-7 rounded-sm text-blue-700 hover:bg-blue-50"
                          data-testid={`edit-${r.item_id}`}
                          title="Edit nominal"
                        >
                          <Wrench className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="outline" onClick={() => removeRow(r.item_id)}
                          className="h-7 w-7 rounded-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                          data-testid={`delete-${r.item_id}`}
                          title="Hapus"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <div className="text-[11px] text-zinc-500 border-l-2 border-blue-300 pl-3 py-1">
        💡 <b>Tip:</b> Perubahan disimpan real-time ke database. Rekap Penagihan akan otomatis pakai nilai baru pada refresh berikutnya, tanpa restart backend atau redeploy production.
      </div>
    </div>
  );
}
